const { default: Scroll } = await import('@utils/scroll')
const { default: A11y } = await import('@utils/a11y')
const { delegateEventListener } = await import('@utils/global')

const DEFAULTS = {
    stackable: false,
    mediaQuery: null,
    lockScroll: true
}

export default class Overlay {
    constructor(element, options = {}) {
        this.bindMethods()

        this.id = element.id
        this.element = element
        this.options = {
            ...DEFAULTS,
            ...options
        }
        this.attributes = {
            'role': this.element.getAttribute('role') ?? 'modal',
            'aria-modal': this.element.getAttribute('aria-modal') ?? 'true',
            'tabindex': this.element.getAttribute('tabindex') ?? -1,
            'inert': this.element.inert,
        }

        if(this.options.mediaQuery) {
            this.options.mediaQuery.addEventListener('change', () => this.options.mediaQuery.matches ? this.enable() : this.disable())
            this.options.mediaQuery.dispatchEvent(new Event('change'))
        } else {
            this.enable()
        }

        delegateEventListener(document, 'click', `[data-open="${this.id}"]`, this.open)
        delegateEventListener(document, 'click', `[data-close="${this.id}"]`, this.close)

        if(!this.options.stackable) {
            addEventListener('opening', (e) => {
                if(e.target !== this.element && this.isOpened) this.close()
            })
        }
    }

    bindMethods() {
        this.handleKeyPress = this.handleKeyPress.bind(this)
        this.handleFocus = this.handleFocus.bind(this)
        this.trapFocus = this.trapFocus.bind(this)
        this.toggle = this.toggle.bind(this)
        this.open = this.open.bind(this)
        this.close = this.close.bind(this)
    }

    get isOpened() {
        return !this.element.inert
    }

    get canLockScroll() {
        return this.options.lockScroll === true || typeof this.options.lockScroll === 'object' && this.options.lockScroll instanceof MediaQueryList && this.options.lockScroll.matches
    }

    enable() {
        for(const [attr, val] of Object.entries(this.attributes)) {
            if(val === null) continue

            this.element.setAttribute(attr, val)
        }

        if(this.isOpened && this.canLockScroll) Scroll.lock()
    }

    disable() {
        if(this.isOpened && this.canLockScroll) Scroll.unlock()

        this.attributes['inert'] = this.element.inert ? '' : null

        Object.keys(this.attributes).forEach(attr => this.element.removeAttribute(attr))
    }
    
    // Dispatch event with id attached
    dispatchEvent(name, data = {}) {
        this.element.dispatchEvent(new CustomEvent(name, {
            detail: data,
            bubbles: true
        }))
    }

    // Handle key presses for keyboard accessibility
    handleKeyPress(e) {
        // Close with escape key
        if(e.key == 'Escape') return this.close()
    }

    // Handle focus events for keyboard accessibility
    handleFocus(e) {
        if(!this.isOpened || this.element.contains(e.target)) return

        const focusableElements = A11y.getFocusableElements(this.element)

        // Go from first to last
        if(e.relatedTarget === focusableElements[0] || e.relatedTarget === this.element) {
            focusableElements[focusableElements.length - 1].focus({
                preventScroll: true
            })
            return
        }

        // Go from last to first
        if(e.relatedTarget === focusableElements[focusableElements.length - 1]) {
            focusableElements[0].focus({
                preventScroll: true
            })
            return
        }
    }

    // Trap keyboard focus inside overlay
    trapFocus() {
        // Store last focused element
        this.returnFocusTo = document.activeElement

        // Add keypress events
        this.element.addEventListener('keydown', this.handleKeyPress)
        addEventListener('focusin', this.handleFocus)

        // Focus overlay
        this.element.focus({
            preventScroll: true
        })
    }

    // Release focus from overlay
    releaseFocus() {
        // Remove keypress events
        this.element.removeEventListener('keydown', this.handleKeyPress)
        removeEventListener('focusin', this.handleFocus)
        
        // Return focus to last focused element
        if(!this.returnFocusTo) return

        this.returnFocusTo.focus()
        this.returnFocusTo = null
    }

    // Toggle overlay
    toggle() {
        this.isOpened ? this.close() : this.open()
    }

    // Open overlay
    open(e) {
        if(e && e.preventDefault) e.preventDefault()

        this.element.inert = false
        
        this.dispatchEvent('opening', {
            id: this.id
        })

        this.trapFocus()
        
        // Request scroll lock
        if(this.canLockScroll) Scroll.lock()
    }

    close(e) {
        if(e && e.preventDefault) e.preventDefault()

        if(!this.isOpened) return

        this.dispatchEvent('closing', {
            id: this.id
        })

        this.releaseFocus()

        // Request scroll unlock
        if(this.canLockScroll) Scroll.unlock()
        
        this.element.inert = true
    }
}